import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { colors } from "../../theme/colors";
import {
    getLoadoutSkillSummary,
    getMapCompletionPercent,
} from "../../utils/csSkillsState";

const BAR_TRACK_SX = {
    flex: 1,
    minWidth: 0,
    height: "0.42rem",
    borderRadius: "999px",
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
};

const BAR_FILL_SX = {
    height: "100%",
    borderRadius: "999px",
    background: `
        linear-gradient(
            90deg,
            #E7B154 0%,
            #F2BE63 60%,
            #FFD486 100%
        )
    `,
};

function MetricRow({ label, value, max = 10, valueLabel, emphasize = false }) {
    const percent = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "5.6rem minmax(0, 1fr) 2.6rem",
                alignItems: "center",
                gap: "0.65rem",
                width: "100%",
            }}
        >
            <Typography
                sx={{
                    fontSize: emphasize ? "0.62rem" : "0.72rem",
                    fontWeight: emphasize ? 700 : 500,
                    letterSpacing: emphasize ? "0.06em" : 0,
                    color: colors.text.secondary,
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
            <Box sx={BAR_TRACK_SX}>
                <Box sx={{ ...BAR_FILL_SX, width: `${percent}%` }} />
            </Box>
            <Typography
                sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: emphasize ? colors.primary.main : colors.text.secondary,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                }}
            >
                {valueLabel}
            </Typography>
        </Box>
    );
}

MetricRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    max: PropTypes.number,
    valueLabel: PropTypes.string.isRequired,
    emphasize: PropTypes.bool,
};

export default function ProfileLoadoutCompletion({ mapData, template }) {
    const skills = getLoadoutSkillSummary(mapData);
    const completion = getMapCompletionPercent(mapData, template);

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: { xs: 0, md: "14rem" },
                maxWidth: { md: "20rem" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "0.55rem",
                [`@media (min-width: 2000px)`]: {
                    minWidth: "18rem",
                    maxWidth: "28rem",
                    gap: "0.7rem",
                },
            }}
        >
            {skills.map((skill) => (
                <MetricRow
                    key={skill.subject}
                    label={skill.subject}
                    value={skill.value}
                    valueLabel={skill.display}
                />
            ))}

            <Box
                sx={{
                    mt: "0.2rem",
                    pt: "0.7rem",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                }}
            >
                <MetricRow
                    label="MAP COMPLETION"
                    value={completion}
                    max={100}
                    valueLabel={`${completion}%`}
                    emphasize
                />
            </Box>
        </Box>
    );
}

ProfileLoadoutCompletion.propTypes = {
    mapData: PropTypes.object,
    template: PropTypes.object,
};
